import isNil from "lodash/isNil";
import { useMemo } from "react";
import type { OptionPriceValue, ProductResponse } from "../../admin/products/productsApi";
import { useProducts } from "../../admin/products/useProducts";
import { useProductsSellingPrices } from "../../calculations/calculationVersionEdit/useProductsSellingPrices";
import type { Currency } from "../../data/BaseEntity";
import dayjs, { ISO_DATE_FORMAT } from "../../dayjs";
import type { PriceValue } from "../../misc/price";
import { Price } from "../../misc/price";
import { getOptionHint } from "../../sharedComponents/products/optionHints";
import type { SelectedProductOption } from "../../sharedComponents/products/price/getOptionPrice";
import { getOptionPrice } from "../../sharedComponents/products/price/getOptionPrice";
import type { OrderPriceValue } from "../ordersApi";
import type { SavedOrder, SavedOrderProduct } from "../orderTypes";
import { getExchangeRate } from "./getExchangeRate";
import type { Product } from "./productsTable/OrderProductsTable";

interface OrderProduct {
  readonly id: string;
  readonly currency?: Currency;
  readonly date: string;
  readonly note?: string;
  readonly options: SelectedProductOption[];
}

export interface Order {
  readonly products: OrderProduct[];
  readonly currency: Currency;
  readonly currencyExchangeRate?: number;
  readonly participantsNumber: { numberOfPaid: number; numberOfStaff: number };
}

function getProductPrice(
  options: { price: PriceValue | undefined; priceListPrice: Price; quantity: number }[],
  exchangeRate: number | undefined,
): Price {
  if (options.length === 0) {
    return new Price(0);
  }
  if (options.some((o) => (isNil(o.price) && isNil(o.priceListPrice)) || o.price === Price.onDemand.value)) {
    return Price.onDemand;
  }
  if (isNil(exchangeRate)) {
    return Price.invalid;
  }
  const price = options
    .map(({ price, priceListPrice, quantity }) => {
      const optionPrice = isNil(price) ? priceListPrice : new Price(price);
      return optionPrice.multiply(quantity);
    })
    .reduce((prev, curr) => prev.add(curr), new Price(0));
  return price.round().multiply(exchangeRate).round();
}

export function getSavedProductPrice(price: OrderPriceValue, exchangeRate: number | undefined): Price {
  if (!exchangeRate) {
    return Price.invalid;
  }
  return Price.fromNullable(price).multiply(exchangeRate).round();
}

function orderProductToProduct(
  orderProduct: OrderProduct,
  orderCurrency: Currency,
  orderCurrencyExchangeRate: number | undefined,
  prices: Record<string, { price: OptionPriceValue }>,
  availableProducts: ProductResponse[],
) {
  const product = availableProducts.find((p) => p.id === orderProduct.id);
  const currency = orderProduct.currency ? orderProduct.currency : product?.currency;
  const exchangeRate = getExchangeRate(currency, orderCurrency, orderCurrencyExchangeRate);
  const options = orderProduct.options.map((o) => ({
    id: o.id,
    quantity: o.quantity,
    price: o.price,
    priceListPrice: getOptionPrice({ id: o.id }, o.id ? prices[o.id]?.price : undefined, 1, 0),
  }));
  return {
    ...orderProduct,
    name: product?.name,
    options: options.map((o) => {
      const matchingOption = product?.options.find((op) => op.id === o.id);
      return {
        ...o,
        name: matchingOption?.name,
        participantListRequired: matchingOption?.participantListRequired ?? false,
        hint: getOptionHint(o, orderCurrency, exchangeRate),
      };
    }),
    exchangeRate,
    price: currency ? getProductPrice(options, exchangeRate) : Price.invalid,
    description: product?.description ?? {},
    contact: product?.contact ?? {},
    images: product?.images ?? [],
  };
}

function savedOrderProductToProduct(
  orderProduct: SavedOrderProduct,
  orderCurrency: Currency,
  orderCurrencyExchangeRate: number | undefined,
  availableProducts: ProductResponse[],
) {
  const product = availableProducts.find((p) => p.id === orderProduct.id);
  const currency = orderProduct.currency;
  const exchangeRate = getExchangeRate(currency, orderCurrency, orderCurrencyExchangeRate);
  return {
    ...orderProduct,
    name: product?.name,
    options: orderProduct.options.map((o) => {
      const matchingOption = product?.options.find((op) => op.id === o.id);
      return {
        ...o,
        name: matchingOption?.name,
        participantListRequired: matchingOption?.participantListRequired ?? false,
        info: matchingOption?.info,
        order: matchingOption?.order,
      };
    }),
    exchangeRate,
    price: currency ? getSavedProductPrice(orderProduct.price, exchangeRate) : Price.invalid,
    description: product?.description ?? {},
    contact: product?.contact ?? {},
    images: product?.images ?? [],
    providerName: product?.provider?.name,
    providerProductType: product?.provider?.productType,
  };
}

interface SelectedProductOptionWithName extends SelectedProductOption {
  readonly name: string | undefined;
  readonly participantListRequired: boolean;
}

interface ProductWithName extends Product {
  readonly name: string | undefined;
  readonly description: ProductResponse["description"];
  readonly contact: ProductResponse["contact"];
  readonly images: ProductResponse["images"];
  readonly options: SelectedProductOptionWithName[];
  readonly providerName?: string;
}

const emptyProducts: ProductWithName[] = [];

export function useOrderToProducts(order: Order): {
  isLoading: boolean;
  products: ProductWithName[];
} {
  const priceQueryInput = useMemo(() => {
    if (!order?.products?.length) {
      return [];
    }
    return order.products
      .filter((p) => !!p.id)
      .map(({ id, date }) => ({
        id,
        dateFrom: date,
        dateTo: dayjs(date, ISO_DATE_FORMAT).add(1, "day").format(ISO_DATE_FORMAT),
      }));
  }, [
    order?.participantsNumber.numberOfPaid,
    order?.participantsNumber.numberOfStaff,
    order?.products?.map((p) => `${p.id}\t${p.date}`).join(",") ?? "",
  ]);

  const totalParticipants = order ? order.participantsNumber.numberOfPaid + order.participantsNumber.numberOfStaff : 0;
  const { prices, isLoading: arePricesLoading } = useProductsSellingPrices(priceQueryInput, totalParticipants);
  const { products, isLoading: areProductsLoading } = useProducts();
  const isLoading = arePricesLoading || areProductsLoading;

  const orderProducts = order?.products;
  const currency = order?.currency;
  const currencyExchangeRate = order?.currencyExchangeRate;
  const result = useMemo(() => {
    if (!orderProducts || !currency) {
      return emptyProducts;
    }
    return orderProducts.map((p) =>
      orderProductToProduct(p, currency, currencyExchangeRate, (prices[p.date] ?? {})[p.id] ?? {}, products),
    );
  }, [orderProducts, currency, currencyExchangeRate, prices, products]);

  return { isLoading, products: result };
}

export function useSavedOrderToProducts(
  order: Pick<SavedOrder, "products" | "currency" | "currencyExchangeRate"> | null | undefined,
): {
  isLoading: boolean;
  products: ProductWithName[];
} {
  const { products, isLoading } = useProducts();
  if (!order) {
    return {
      isLoading,
      products: [],
    };
  }
  return {
    isLoading,
    products: order.products.map((p) =>
      savedOrderProductToProduct(p, order.currency, order.currencyExchangeRate, products),
    ),
  };
}
